import React from 'react';
import { Bot, User, Shield, Paperclip, Check, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

function roleIcon(role) {
  if (role === 'ai') return <Bot className="w-3.5 h-3.5 text-violet-500" />;
  if (role === 'admin') return <Shield className="w-3.5 h-3.5 text-[#0047AB]" />;
  return <User className="w-3.5 h-3.5 text-slate-400" />;
}

function roleBg(role) {
  if (role === 'ai') return 'bg-violet-50 border-violet-100';
  if (role === 'admin') return 'bg-blue-50 border-blue-100';
  return 'bg-white border-slate-200';
}

export function NpsSurveyCard({ npsScore, setNpsScore, npsComment, setNpsComment, npsSubmitted, npsSubmitting, onSubmit }) {
  if (npsSubmitted) {
    return (
      <div className="mx-3 my-2 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-center" data-testid="nps-thanks">
        <Check className="w-6 h-6 text-emerald-500 mx-auto mb-1" />
        <p className="text-xs font-semibold text-emerald-700">Thank you for your feedback!</p>
        <p className="text-[10px] text-emerald-500 mt-0.5">Your response helps us improve.</p>
      </div>
    );
  }
  return (
    <div className="mx-3 my-2 p-4 bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="nps-survey-card">
      <p className="text-xs font-semibold text-slate-900 mb-1">How was your support experience?</p>
      <p className="text-[10px] text-slate-400 mb-3">Rate from 0 (not likely) to 10 (very likely) to recommend us</p>
      <div className="flex justify-between gap-0.5 mb-3">
        {[0,1,2,3,4,5,6,7,8,9,10].map(n => {
          let selectedClass = 'bg-red-500 text-white';
          if (n >= 9) selectedClass = 'bg-emerald-500 text-white';
          else if (n >= 7) selectedClass = 'bg-amber-500 text-white';
          const buttonClass = npsScore === n ? selectedClass : 'bg-slate-100 text-slate-500 hover:bg-slate-200';
          return (
            <button
              key={n}
              onClick={() => setNpsScore(n)}
              className={`w-7 h-7 rounded text-[10px] font-bold transition-all ${buttonClass}`}
              data-testid={`nps-score-${n}`}
            >{n}</button>
          );
        })}
      </div>
      <div className="flex justify-between text-[9px] text-slate-300 mb-3">
        <span>Not likely</span><span>Very likely</span>
      </div>
      <Textarea
        value={npsComment}
        onChange={e => setNpsComment(e.target.value)}
        placeholder="Any comments? (optional)"
        className="text-xs rounded-md mb-2 h-16 resize-none"
        data-testid="nps-comment"
      />
      <Button
        onClick={onSubmit}
        disabled={npsScore === null || npsSubmitting}
        className="w-full h-8 text-xs bg-[#0047AB] hover:bg-[#003580] rounded-md"
        data-testid="nps-submit-btn"
      >
        {npsSubmitting ? 'Submitting...' : 'Submit Feedback'}
      </Button>
    </div>
  );
}

export function ChatMessages({ messages, messagesEndRef, npsProps }) {
  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50/50">
      {messages.length === 0 && (
        <div className="text-center py-8">
          <Bot className="w-8 h-8 text-violet-300 mx-auto mb-2" />
          <p className="text-xs text-slate-400">Hi! I'm your AI support assistant.</p>
          <p className="text-[10px] text-slate-300 mt-1">Ask me anything about Real Leads CRM, or type "talk to admin" for human support.</p>
        </div>
      )}
      {messages.map(msg => {
        if (msg.content === '__NPS_SURVEY__') return <NpsSurveyCard key={msg.id} {...npsProps} />;
        return (
          <div key={msg.id} className={`flex ${msg.sender_role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-lg border text-sm ${msg.sender_role === 'user' ? 'bg-[#0047AB] text-white border-[#0047AB]' : roleBg(msg.sender_role)}`}>
              {msg.sender_role !== 'user' && (
                <div className="flex items-center gap-1 mb-0.5">
                  {roleIcon(msg.sender_role)}
                  <span className="text-[10px] font-medium opacity-70">{msg.sender_name}</span>
                </div>
              )}
              <p className="whitespace-pre-wrap text-[13px] leading-relaxed">{msg.content}</p>
              {msg.attachment_path && (
                <p className="text-[10px] mt-1 opacity-60 flex items-center gap-1"><Paperclip className="w-2.5 h-2.5" /> Attachment</p>
              )}
              <p className={`text-[9px] mt-1 ${msg.sender_role === 'user' ? 'opacity-50' : 'text-slate-400'}`}>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
            </div>
          </div>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
}
